import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import CloseIcon from "@mui/icons-material/Close";
import ChecklistIcon from "@mui/icons-material/Checklist";
import PlaceIcon from "@mui/icons-material/Place";
import BoltIcon from "@mui/icons-material/Bolt";

function TaskTemplateImportModal(props) {
  const isAdmin =
    props.permissionRole === "company_admin" ||
    props.permissionRole === "rightwork_internal";

  const companyStores = React.useMemo(() => {
    return (props.storeNames || []).filter((s) => s.company === props.homeCompany);
  }, [props.storeNames, props.homeCompany]);

  const managedStoreIds = React.useMemo(() => {
    if (isAdmin) return null;
    const managerPermissions = ["company_manager", "company_admin", "rightwork_internal"];
    return new Set(
      (props.storeOptions || [])
        .filter((m) => managerPermissions.includes(m.permission_type))
        .map((m) => m.store)
    );
  }, [isAdmin, props.storeOptions]);

  const availableStores = React.useMemo(() => {
    if (isAdmin) return companyStores;
    return companyStores.filter((store) => managedStoreIds?.has(store.id));
  }, [isAdmin, companyStores, managedStoreIds]);

  const getDefaultStoreIds = React.useCallback(() => {
    if (isAdmin) return [];
    const homeStore = availableStores.find((store) => store.id === props.homeStore);
    if (homeStore) return [homeStore.id];
    if (availableStores.length > 0) return [availableStores[0].id];
    return [];
  }, [isAdmin, availableStores, props.homeStore]);

  const [selectedTemplateKeys, setSelectedTemplateKeys] = React.useState([]);
  const [scope, setScope] = React.useState(isAdmin ? "company" : "store");
  const [selectedStoreIds, setSelectedStoreIds] = React.useState(getDefaultStoreIds());

  React.useEffect(() => {
    if (!props.open) return;
    setSelectedTemplateKeys([]);
    setScope(isAdmin ? "company" : "store");
    setSelectedStoreIds(getDefaultStoreIds());
  }, [props.open, isAdmin, getDefaultStoreIds]);

  const storeNameById = React.useMemo(() => {
    const lookup = {};
    companyStores.forEach((store) => {
      lookup[store.id] = store.store_name;
    });
    return lookup;
  }, [companyStores]);

  const groupLabelByKey = React.useMemo(() => {
    const lookup = {};
    (props.templateGroups || []).forEach((group) => {
      lookup[group.key] = group.label;
    });
    return lookup;
  }, [props.templateGroups]);

  const groupedTemplates = React.useMemo(() => {
    const grouped = {};
    (props.taskTemplates || []).forEach((template) => {
      const group = template.group || "other";
      if (!grouped[group]) grouped[group] = [];
      grouped[group].push(template);
    });
    return grouped;
  }, [props.taskTemplates]);

  const orderedGroupKeys = React.useMemo(() => {
    const configuredGroupKeys = (props.templateGroups || []).map((group) => group.key);
    const remainingGroupKeys = Object.keys(groupedTemplates).filter(
      (groupKey) => !configuredGroupKeys.includes(groupKey)
    );
    return [...configuredGroupKeys, ...remainingGroupKeys].filter(
      (groupKey) => groupedTemplates[groupKey]?.length > 0
    );
  }, [props.templateGroups, groupedTemplates]);

  const isTemplateCoveredForScope = (template) => {
    if (template.is_imported_company_wide) return true;
    if (scope === "company") return false;
    if (selectedStoreIds.length === 0) return false;
    const importedStoreSet = new Set(template.imported_store_ids || []);
    return selectedStoreIds.every((storeId) => importedStoreSet.has(storeId));
  };

  const getTemplateStatus = (template) => {
    if (template.is_imported_company_wide) return "Imported company-wide";
    if ((template.imported_store_ids || []).length === 0) return null;

    if (
      scope === "store" &&
      selectedStoreIds.length > 0 &&
      selectedStoreIds.every((storeId) => (template.imported_store_ids || []).includes(storeId))
    ) {
      return "Imported for selected locations";
    }

    const importedNames = (template.imported_store_ids || [])
      .map((storeId) => storeNameById[storeId])
      .filter(Boolean);
    if (importedNames.length === 0) return "Imported at another location";
    if (importedNames.length <= 2) return `Imported at ${importedNames.join(", ")}`;
    return `Imported at ${importedNames.length} locations`;
  };

  const toggleTemplate = (template) => {
    if (isTemplateCoveredForScope(template)) return;
    setSelectedTemplateKeys((prevKeys) => {
      if (prevKeys.includes(template.key)) {
        return prevKeys.filter((key) => key !== template.key);
      }
      return [...prevKeys, template.key];
    });
  };

  const handleTemplateKeyDown = (event, template) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    toggleTemplate(template);
  };

  const handleScopeChange = (event) => {
    const nextScope = event.target.value;
    setScope(nextScope);
    if (nextScope === "store" && selectedStoreIds.length === 0) {
      setSelectedStoreIds(getDefaultStoreIds());
    }
  };

  const handleImport = async () => {
    await props.import_plan_task_templates(
      selectedTemplateKeys,
      scope === "store",
      scope === "store" ? selectedStoreIds : []
    );
    props.onClose();
  };

  const selectedImportableTemplateKeys = selectedTemplateKeys.filter((templateKey) => {
    const template = (props.taskTemplates || []).find((t) => t.key === templateKey);
    return template && !isTemplateCoveredForScope(template);
  });

  const isImportDisabled =
    selectedImportableTemplateKeys.length === 0 ||
    (scope === "store" && selectedStoreIds.length === 0);

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ padding: 0 }}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Import Task Templates</h2>
            <p className="text-sm text-slate-500 mt-1">
              Starter checklists for restaurant opening, closing, cleaning, restrooms, and admin work.
            </p>
          </div>
          <button
            className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            onClick={props.onClose}
          >
            <CloseIcon style={{ fontSize: "20px" }} />
          </button>
        </div>
      </DialogTitle>

      <DialogContent sx={{ padding: 0 }}>
        <div className="px-6 py-4 border-b border-slate-100 flex flex-wrap items-center gap-3">
          <Select
            value={scope}
            onChange={handleScopeChange}
            size="small"
            disabled={!isAdmin}
            sx={{
              minWidth: 170,
              fontSize: "0.875rem",
              borderRadius: "8px",
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6d28d9",
              },
            }}
          >
            {isAdmin && <MenuItem value="company">Company-wide</MenuItem>}
            <MenuItem value="store">Location-specific</MenuItem>
          </Select>

          {scope === "store" && (
            <Select
              multiple
              displayEmpty
              value={selectedStoreIds}
              onChange={(event) => setSelectedStoreIds(event.target.value)}
              size="small"
              renderValue={(selected) => {
                if (selected.length === 0) return "Select locations";
                return selected
                  .map((storeId) => storeNameById[storeId])
                  .filter(Boolean)
                  .join(", ");
              }}
              sx={{
                minWidth: 260,
                fontSize: "0.875rem",
                borderRadius: "8px",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6d28d9",
                },
              }}
            >
              {availableStores.map((store) => (
                <MenuItem key={store.id} value={store.id}>
                  <Checkbox
                    checked={selectedStoreIds.includes(store.id)}
                    size="small"
                    sx={{
                      color: "#9ca3af",
                      "&.Mui-checked": {
                        color: "#6d28d9",
                      },
                    }}
                  />
                  <ListItemText primary={store.store_name} primaryTypographyProps={{ fontSize: "0.875rem" }} />
                </MenuItem>
              ))}
            </Select>
          )}

          <p className="text-sm text-slate-500 ml-auto">
            {selectedImportableTemplateKeys.length} selected
          </p>
        </div>

        <div className="px-6 py-5 max-h-[68vh] overflow-y-auto">
          {orderedGroupKeys.map((groupKey) => (
            <div key={groupKey} className="mb-7 last:mb-0">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-sm font-semibold text-slate-900">
                  {groupLabelByKey[groupKey] || groupKey}
                </h3>
                <div className="h-px bg-slate-200 flex-1"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {groupedTemplates[groupKey].map((template) => {
                  const isCovered = isTemplateCoveredForScope(template);
                  const isSelected = selectedImportableTemplateKeys.includes(template.key);
                  const status = getTemplateStatus(template);

                  return (
                    <div
                      key={template.key}
                      role="button"
                      tabIndex={isCovered ? -1 : 0}
                      className={`text-left rounded-lg border p-4 transition-colors ${
                        isCovered
                          ? "border-slate-200 bg-slate-50 opacity-70"
                          : isSelected
                            ? "border-violet-600 bg-violet-50"
                            : "border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50/40"
                      }`}
                      onClick={() => toggleTemplate(template)}
                      onKeyDown={(event) => handleTemplateKeyDown(event, template)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">{template.name}</p>
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                            {template.description}
                          </p>
                        </div>
                        <Checkbox
                          checked={isSelected || isCovered}
                          disabled={isCovered}
                          size="small"
                          sx={{
                            padding: 0,
                            color: "#cbd5e1",
                            "&.Mui-checked": {
                              color: isCovered ? "#94a3b8" : "#6d28d9",
                            },
                          }}
                        />
                      </div>

                      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1 rounded-md bg-white border border-slate-200 px-2 py-1">
                          <ChecklistIcon style={{ fontSize: "14px" }} />
                          {template.subtask_count} subtasks
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-md bg-white border border-slate-200 px-2 py-1">
                          {template.schedule_summary}
                        </span>
                        {template.is_auto_assignable && (
                          <span className="inline-flex items-center gap-1 rounded-md bg-white border border-slate-200 px-2 py-1">
                            <BoltIcon style={{ fontSize: "14px" }} />
                            Auto
                          </span>
                        )}
                        {scope === "store" && (
                          <span className="inline-flex items-center gap-1 rounded-md bg-white border border-slate-200 px-2 py-1">
                            <PlaceIcon style={{ fontSize: "14px" }} />
                            Location
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-slate-500 mt-3">{template.time_window_summary}</p>
                      {status && (
                        <p className={`text-xs mt-2 ${isCovered ? "text-slate-500" : "text-amber-600"}`}>
                          {status}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>

      <DialogActions sx={{ padding: 0 }}>
        <div className="px-6 py-4 border-t border-slate-200 w-full flex items-center justify-end gap-2">
          <button
            className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
            onClick={props.onClose}
          >
            Cancel
          </button>
          <button
            className={`px-4 py-2 rounded-lg text-sm text-white ${
              isImportDisabled
                ? "bg-slate-300 cursor-not-allowed"
                : "bg-violet-700 hover:bg-violet-800"
            }`}
            disabled={isImportDisabled}
            onClick={handleImport}
          >
            Import Templates
          </button>
        </div>
      </DialogActions>
    </Dialog>
  );
}

export default TaskTemplateImportModal;
